import { getText } from "shared/lib";
import {
  TreePropertyType,
  updateTreeProperty,
  type TreeDescriptor,
  type TrunkTypeTreePropertyDescriptor,
} from "../lib";
import { useTreeDialogValidator } from "./lib";

import { Toggle } from "shared/components";
import { FieldLabel } from "./FieldLabel";
import { FieldValidationMessages } from "./FieldValidationMessages";

export interface TrunkTypeFieldProps {
  includeVegetation?: boolean;
  label?: string;
  onChange: ((treeDescriptor: TreeDescriptor) => void) | undefined;
  treeDescriptor: TreeDescriptor;
}

export function TrunkTypeField<T extends TrunkTypeTreePropertyDescriptor>({
  includeVegetation,
  label,
  onChange,
  treeDescriptor,
}: TrunkTypeFieldProps) {
  const { isValid, messages, shouldShowValidation } = useTreeDialogValidator<T>(
    {
      checker: (property) => !!property?.value,
      properties: treeDescriptor.properties,
      treePropertyType: TreePropertyType.trunk_type,
    },
  );

  const trunkType = treeDescriptor.properties.get(
    TreePropertyType.trunk_type,
  ) as T | undefined;

  const handleChange = (value: string) => {
    let newTreeDescriptor = updateTreeProperty<T>(
      treeDescriptor,
      TreePropertyType.trunk_type,
      (draft) => {
        draft.value = value as T["value"];
      },
    );

    // Clear DBH, circumference, and height fields when switching to vegetation
    if (value === "vegetation") {
      if (newTreeDescriptor.properties.has(TreePropertyType.dbh)) {
        newTreeDescriptor = updateTreeProperty(
          newTreeDescriptor,
          TreePropertyType.dbh,
          (draft) => {
            draft.value = "";
          },
        );
      }

      if (newTreeDescriptor.properties.has(TreePropertyType.circumference)) {
        newTreeDescriptor = updateTreeProperty(
          newTreeDescriptor,
          TreePropertyType.circumference,
          (draft) => {
            draft.value = "";
          },
        );
      }

      if (newTreeDescriptor.properties.has(TreePropertyType.height)) {
        newTreeDescriptor = updateTreeProperty(
          newTreeDescriptor,
          TreePropertyType.height,
          (draft) => {
            draft.value = "";
          },
        );
      }
    }

    onChange?.(newTreeDescriptor);
  };

  return (
    <div className="flex flex-col gap-1">
      <FieldLabel isValid={isValid}>
        {label || getText("Trunk type")}
      </FieldLabel>

      <div className="flex gap-2">
        <Toggle
          aria-invalid={!isValid && shouldShowValidation}
          className="aria-invalid:border-redBorder"
          onClick={() => {
            handleChange("single");
          }}
          pressed={trunkType?.value === "single"}
          variant="rounded"
        >
          {getText("Single stem")}
        </Toggle>

        <Toggle
          aria-invalid={!isValid && shouldShowValidation}
          className="aria-invalid:border-redBorder"
          onClick={() => {
            handleChange("multi");
          }}
          pressed={trunkType?.value === "multi"}
          variant="rounded"
        >
          {getText("Multi stem")}
        </Toggle>

        {includeVegetation && (
          <Toggle
            aria-invalid={!isValid && shouldShowValidation}
            className="aria-invalid:border-redBorder"
            onClick={() => {
              handleChange("vegetation");
            }}
            pressed={trunkType?.value === "vegetation"}
            variant="rounded"
          >
            {getText("Vegetation")}
          </Toggle>
        )}
      </div>

      <FieldValidationMessages messages={messages} />
    </div>
  );
}
